import { create } from 'zustand'
import { Recipe, RecipeFormData } from '@/types/recipe'
import {
  getRecipesFiltered,
  addRecipe as addRecipeService,
  updateRecipe as updateRecipeService,
  deleteRecipe as deleteRecipeService,
  toggleUserFavorite as toggleUserFavoriteService,
  toggleShare as toggleShareService,
  getRecipeById as getRecipeByIdService // Import the new service function
} from '@/lib/supabase-service'

// Debounce timer for search
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
const SEARCH_DEBOUNCE_MS = 300;

interface RecipeFilters {
  searchQuery: string
  category: string
  showFavoritesOnly: boolean
  showOwnRecipesOnly: boolean
}

interface PaginationState {
  limit: number
  totalCount: number
  offset: number
  currentPage: number
}

interface RecipeStore {
  recipes: Recipe[]
  studioRecipes: Recipe[]
  loading: boolean
  gridLoading: boolean
  error: string | null
  filters: RecipeFilters
  pagination: PaginationState
  
  // Actions
  fetchRecipes: () => Promise<void>
  fetchStudioRecipes: () => Promise<void>
  addRecipe: (recipeData: RecipeFormData) => Promise<Recipe>
  updateRecipe: (id: string, updates: Partial<RecipeFormData>) => Promise<void>
  deleteRecipe: (id: string) => Promise<void>
  toggleFavorite: (id: string) => Promise<void>
  toggleShare: (id: string, currentValue: boolean) => Promise<void>
  fetchRecipeById: (id: string) => Promise<Recipe | null>
  
  // Filter actions
  setSearchQuery: (query: string) => void
  setCategory: (category: string) => void
  setShowFavoritesOnly: (show: boolean) => void
  setShowOwnRecipesOnly: (show: boolean) => void
  
  // Limit actions
  setLimit: (limit: number) => Promise<void>
  // Pagination actions
  goToPage: (page: number) => void
  nextPage: () => void
  previousPage: () => void
  // Computed
  getFilteredRecipes: () => Recipe[]
  getRecipeById: (id: string) => Recipe | undefined
  
  // Utility
  clearError: () => void
}

export const useRecipeStore = create<RecipeStore>((set, get) => ({
  recipes: [],
  studioRecipes: [],
  loading: false,
  gridLoading: false,
  error: null,
  filters: {
    searchQuery: '',
    category: 'all',
    showFavoritesOnly: false,
    showOwnRecipesOnly: false,
  },
  pagination: {
    limit: 25,
    totalCount: 0,
    offset: 0,
    currentPage: 1,
  },

  fetchRecipes: async () => {
    const { pagination: currentPagination, filters } = get();
    const isInitialLoad = get().recipes.length === 0;
    if (isInitialLoad) {
      set({ loading: true, error: null });
    }
    try {
      const result = await getRecipesFiltered(
        currentPagination.limit,
        currentPagination.offset,
        filters.searchQuery,
        filters.category,
        filters.showFavoritesOnly,
        filters.showOwnRecipesOnly
      );
      set({
        recipes: result.data,
        loading: false,
        gridLoading: false,
        pagination: {
          ...currentPagination,
          totalCount: result.totalCount
        }
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch recipes',
        loading: false,
        gridLoading: false
      })
    }
  },

  fetchStudioRecipes: async () => {
    try {
      const result = await getRecipesFiltered(50, 0, '', 'all', false, false);
      const studioOnly = result.data.filter((r: Recipe) => r.source === 'studio').slice(0, 4);
      set({ studioRecipes: studioOnly });
    } catch {
    }
  },

  addRecipe: async (recipeData: RecipeFormData) => {
    set({ loading: true, error: null })
    try {
      const newRecipe = await addRecipeService(recipeData)
      set(state => ({
        recipes: [newRecipe, ...state.recipes],
        loading: false
      }))
      // Refresh studio recipes if this is a studio recipe
      if (recipeData.source === 'studio') {
        get().fetchStudioRecipes();
      }
      return newRecipe
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to add recipe',
        loading: false
      })
      throw error
    }
  },

  updateRecipe: async (id: string, updates: Partial<RecipeFormData>) => {
    set({ loading: true, error: null })
    try {
      const updatedRecipe = await updateRecipeService(id, updates)
      set(state => ({
        recipes: state.recipes.map(recipe =>
          recipe.id === id ? updatedRecipe : recipe
        ),
        loading: false
      }))
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update recipe',
        loading: false
      })
      throw error
    }
  },

  deleteRecipe: async (id: string) => {
    set({ loading: true, error: null })
    try {
      await deleteRecipeService(id)
      set(state => ({
        recipes: state.recipes.filter(recipe => recipe.id !== id),
        studioRecipes: state.studioRecipes.filter(recipe => recipe.id !== id),
        pagination: {
          ...state.pagination,
          totalCount: Math.max(0, state.pagination.totalCount - 1),
        },
        loading: false
      }))
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to delete recipe',
        loading: false
      })
      throw error
    }
  },

  toggleFavorite: async (id: string) => {
    const recipe = get().recipes.find(r => r.id === id)
    if (!recipe) return

    try {
      await toggleUserFavoriteService(id)
      // Update local state immediately like toggleShare does
      set(state => ({
        recipes: state.recipes.map(r =>
          r.id === id ? { ...r, isFavorite: !r.isFavorite } : r
        )
      }))
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to toggle favorite'
      })
      throw error
    }
  },

  toggleShare: async (id: string, currentValue: boolean) => {
    try {
      // We still need to find the recipe to update it in the local state,
      // but we use the `currentValue` passed to the function for the service call.
      const recipeExists = get().recipes.some(r => r.id === id)
      if (!recipeExists) {
        // We can still proceed with the backend call and let it handle the logic.
      }

      // Call the service with the explicit current value.
      await toggleShareService(id, currentValue)

      // Optimistically update the local state.
      set(state => ({
        recipes: state.recipes.map(r =>
          r.id === id ? { ...r, share: !r.share } : r
        )
      }))
    } catch (error) {
      // Optionally revert the optimistic update on failure, though this is complex.
      // For now, just log the error and set an error state.
      set({
        error: error instanceof Error ? error.message : 'Failed to toggle share'
      })
      throw error // Re-throw so the calling component can handle it if needed
    }
  },

  fetchRecipeById: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const recipe = await getRecipeByIdService(id);
      if (recipe) {
        set(state => {
          const recipeExists = state.recipes.find(r => r.id === recipe.id);
          if (recipeExists) {
            // Update existing recipe
            return {
              recipes: state.recipes.map(r => r.id === recipe.id ? recipe : r),
              loading: false,
            };
          } else {
            // Add new recipe to the store
            return {
              recipes: [...state.recipes, recipe],
              loading: false,
            };
          }
        });
      } else {
        // Recipe not found by service
        set({ loading: false });
      }
      return recipe; // Return the fetched recipe or null
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : `Failed to fetch recipe ${id}`,
        loading: false,
      });
      return null; // Or rethrow, depending on desired error handling for the caller
    }
  },

  setSearchQuery: (searchQuery: string) => {
    set(state => ({
      filters: { ...state.filters, searchQuery },
      pagination: { ...state.pagination, currentPage: 1, offset: 0 } // Reset pagination
    }));
    // Debounced fetch to prevent flooding API on every keystroke
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }
    searchDebounceTimer = setTimeout(() => {
      get().fetchRecipes();
    }, SEARCH_DEBOUNCE_MS);
  },

  setCategory: (category: string) => {
    set(state => ({
      filters: { ...state.filters, category },
      pagination: { ...state.pagination, currentPage: 1, offset: 0 } // Reset pagination
    }));
    // Trigger refetch with new filters
    setTimeout(() => get().fetchRecipes(), 0)
  },

  setShowFavoritesOnly: (showFavoritesOnly: boolean) => {
    set(state => ({
      filters: { ...state.filters, showFavoritesOnly },
      pagination: { ...state.pagination, currentPage: 1, offset: 0 } // Reset pagination
    }));
    // Trigger refetch with new filters
    setTimeout(() => get().fetchRecipes(), 0)
  },

  setShowOwnRecipesOnly: (showOwnRecipesOnly: boolean) => {
    set(state => ({
      filters: { ...state.filters, showOwnRecipesOnly },
      pagination: { ...state.pagination, currentPage: 1, offset: 0 } // Reset pagination
    }));
    // Trigger refetch with new filters
    setTimeout(() => get().fetchRecipes(), 0)
  },

  getFilteredRecipes: () => {
    // Since filtering is now done at the database level, just return all recipes
    return get().recipes
  },

  getRecipeById: (id: string) => {
    return get().recipes.find(recipe => recipe.id === id)
  },

  setLimit: async (newLimit: number) => {
    const { filters } = get();
    const newOffset = 0;
    const newCurrentPage = 1;

    set({ gridLoading: true, error: null });
    try {
      const result = await getRecipesFiltered(
        newLimit,
        newOffset,
        filters.searchQuery,
        filters.category,
        filters.showFavoritesOnly,
        filters.showOwnRecipesOnly
      );
      set({
        recipes: result.data,
        pagination: {
          limit: newLimit,
          offset: newOffset,
          currentPage: newCurrentPage,
          totalCount: result.totalCount
        },
        gridLoading: false
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load recipes',
        gridLoading: false
      })
    }
  },

  // Pagination actions
  goToPage: (page: number) => {
    const { pagination } = get();
    const newOffset = (page - 1) * pagination.limit;
    
    if (page === 1 || (newOffset >= 0 && newOffset < pagination.totalCount) || (pagination.totalCount === 0 && page === 1 && newOffset === 0) ) {
      set(state => ({
        pagination: { ...state.pagination, currentPage: page, offset: newOffset }
      }));
      get().fetchRecipes();
    }
  },

  nextPage: () => {
    const { pagination } = get();
    const totalPages = Math.ceil(pagination.totalCount / pagination.limit);
    if (pagination.currentPage < totalPages) {
      const nextPageNumber = pagination.currentPage + 1;
      const newOffset = (nextPageNumber - 1) * pagination.limit;
      set(state => ({
        pagination: { ...state.pagination, currentPage: nextPageNumber, offset: newOffset }
      }));
      get().fetchRecipes();
    }
  },

  previousPage: () => {
    const { pagination } = get();
    if (pagination.currentPage > 1) {
      const prevPageNumber = pagination.currentPage - 1;
      const newOffset = (prevPageNumber - 1) * pagination.limit;
      set(state => ({
        pagination: { ...state.pagination, currentPage: prevPageNumber, offset: newOffset }
      }));
      get().fetchRecipes();
    }
  },

  clearError: () => {
    set({ error: null })
  }
}))
